// -- Tree View ------------------------------------------------
// Keyboard navigation and ARIA state for tree views, plus the named-state
// API bound per branch (<details class="tree-branch">), so agents/tests can
// expand branches by name (AGENTS.md "State API"). Opt-in on the .tree:
// data-selectable (single selection), data-checkable (checkboxes that
// cascade to children and roll up to parents - tri-state) and data-sortable
// (drag & drop reordering with the native Drag and Drop API, Alt+Arrow keys).

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals } from '../../shared/state-api.js';

const df$ = defussGlobals();

const treeViewStates = ['default', 'expanded'];

/**
 * UI side of setState (per branch): 'expanded' opens the branch, 'default'
 * restores the authored open/closed snapshot taken at init. The <details>
 * toggle event keeps aria-expanded on the treeitem in sync automatically.
 */
function triggerStateChange(details, stateName, _config) {
  switch (stateName) {
    case 'default':
      details.open = details._defaultOpen ?? false;
      break;
    case 'expanded':
      details.open = true;
      break;
  }
}

/** Registry-level API; pass the branch element explicitly. Unknown names throw. */
export const treeViewApi = {
  setState(details, stateName, config = {}) {
    if (!treeViewStates.includes(stateName)) {
      throw new Error(`tree-view: unknown state "${stateName}" (supported: ${treeViewStates.join(', ')})`);
    }
    triggerStateChange(details, stateName, config);
    // state lives on the ELEMENT, not the module (many branches per tree)
    details.dataset.stateName = stateName;
    details._stateConfig = config;
  },
  getState(details) {
    // reflect reality: summary clicks and ArrowLeft/Right change it too
    return {
      name: details.open ? 'expanded' : 'default',
      config: details._stateConfig ?? {},
    };
  },
};

df$.treeViewApi = treeViewApi;
df$.treeViewStates = treeViewStates;

/** The treeitem a row (branch trigger or leaf) belongs to. */
const itemOf = (row) => row.closest('[role="treeitem"]');
/** aria-disabled="true" on the treeitem: focusable, never operable. */
const isDisabled = (item) => item?.getAttribute('aria-disabled') === 'true';

/**
 * Single selection (APG tree, opt-in via data-selectable on .tree): the
 * chosen treeitem gets aria-selected="true", every other selectable item
 * "false"; disabled items are skipped. Announces the choice as a bubbling
 * `tree-select` CustomEvent ({ detail: { item } }) - selection is an
 * interaction on the tree, not a per-branch State API state.
 */
function selectItem(tree, item) {
  if (!item || isDisabled(item) || item.getAttribute('aria-selected') === 'true') return;
  tree.querySelectorAll('[role="treeitem"][aria-selected="true"]').forEach((other) => other.setAttribute('aria-selected', 'false'));
  item.setAttribute('aria-selected', 'true');
  tree.dispatchEvent(new CustomEvent('tree-select', { bubbles: true, detail: { item } }));
}


/* -- Checkboxes (data-checkable) ------------------------------------------
   A checkbox in a row (.tree-check, after the icons, before the label).
   Checking a folder checks everything under it; a folder's own box shows
   checked / unchecked / indeterminate from its children. data-checkable=
   "independent" turns the cascade off. The inputs stay real form controls
   (name / value submit natively). */
const checkOf = (item) => item?.querySelector(':scope > .tree-leaf > .tree-check, :scope > details > .tree-branch-trigger > .tree-check');
const childItems = (item) => [...(item.querySelector(':scope > details > .tree-group')?.children ?? [])].filter((li) => li.matches('[role="treeitem"]'));
const cascades = (tree) => tree.dataset.checkable !== 'independent';

/** Down: a folder's state to every descendant box. */
function checkDown(item, checked) {
  for (const child of childItems(item)) {
    const box = checkOf(child);
    if (box && !box.disabled) {
      box.checked = checked;
      box.indeterminate = false;
    }
    checkDown(child, checked);
  }
}
/** Up: every ancestor folder derives its box from its children. */
function rollUp(tree, item) {
  let parent = item.parentElement?.closest('[role="treeitem"]');
  while (parent && tree.contains(parent)) {
    const box = checkOf(parent);
    if (box) {
      const kids = childItems(parent).map(checkOf).filter(Boolean);
      const on = kids.filter((k) => k.checked && !k.indeterminate).length;
      const mixed = kids.some((k) => k.indeterminate);
      box.checked = kids.length > 0 && on === kids.length;
      box.indeterminate = mixed || (on > 0 && on < kids.length);
    }
    parent = parent.parentElement?.closest('[role="treeitem"]');
  }
}
function syncAria(tree) {
  tree.querySelectorAll('[role="treeitem"]').forEach((item) => {
    const box = checkOf(item);
    if (box) item.setAttribute('aria-checked', box.indeterminate ? 'mixed' : String(box.checked));
  });
}
function checkedValues(tree) {
  return [...tree.querySelectorAll('.tree-check')]
    .filter((b) => b.checked && !b.indeterminate)
    .map((b) => b.value !== 'on' ? b.value : b.closest('[role="treeitem"]').querySelector(':scope > * > span:last-child, :scope > details > summary > span:last-child')?.textContent ?? '');
}
function onCheck(tree, item) {
  const box = checkOf(item);
  if (!box) return;
  box.indeterminate = false;
  if (cascades(tree)) {
    checkDown(item, box.checked);
    rollUp(tree, item);
  }
  syncAria(tree);
  tree.dispatchEvent(new CustomEvent('tree-check', { bubbles: true, detail: { item, checked: box.checked, values: checkedValues(tree) } }));
}
function initChecks(tree) {
  let n = 0;
  tree.querySelectorAll('.tree-check').forEach((box) => {
    // the row's label names the checkbox
    if (!box.hasAttribute('aria-label') && !box.hasAttribute('aria-labelledby')) {
      const label = box.parentElement.querySelector(':scope > span:last-child');
      if (label) {
        label.id ||= `${tree.id || 'tree'}-lbl-${n++}-${Math.random().toString(36).slice(2, 7)}`;
        box.setAttribute('aria-labelledby', label.id);
      }
    }
  });
  if (cascades(tree)) {
    // authored checked folders cascade down, then every folder rolls up
    tree.querySelectorAll('[role="treeitem"]').forEach((item) => { const b = checkOf(item); if (b?.checked) checkDown(item, true); });
    const leaves = [...tree.querySelectorAll('[role="treeitem"]')].filter((i) => !childItems(i).length);
    leaves.forEach((leaf) => rollUp(tree, leaf));
  }
  syncAria(tree);
  tree.addEventListener('change', (e) => {
    if (!e.target.matches?.('.tree-check')) return;
    onCheck(tree, itemOf(e.target));
  });
}

/* -- Drag & drop reordering (data-sortable) -------------------------------
   Rows are draggable (native Drag and Drop API). Dropping on the upper /
   lower quarter of a row puts the item before / after it; dropping on the
   middle of a folder moves it into that folder (opened). A folder can't
   move into itself. Alt+ArrowUp / Alt+ArrowDown moves the focused item
   among its siblings. Every move fires a bubbling tree-reorder event. */
function clearDrop(tree) {
  tree.querySelectorAll('[data-drop]').forEach((r) => r.removeAttribute('data-drop'));
}
function announceMove(tree, item) {
  const parentItem = item.parentElement.closest('[role="treeitem"]');
  const index = [...item.parentElement.children].indexOf(item);
  tree.dispatchEvent(new CustomEvent('tree-reorder', { bubbles: true, detail: { item, parent: parentItem ?? tree, index } }));
}
function initSortable(tree) {
  let dragged = null;
  const rows = () => tree.querySelectorAll('.tree-branch-trigger, .tree-leaf');
  rows().forEach((row) => { if (!isDisabled(itemOf(row))) row.draggable = true; });
  tree.addEventListener('dragstart', (e) => {
    const row = e.target.closest?.('.tree-branch-trigger, .tree-leaf');
    if (!row) return;
    dragged = itemOf(row);
    dragged.dataset.dragging = '';
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', row.textContent.trim());
  });
  tree.addEventListener('dragover', (e) => {
    const row = e.target.closest?.('.tree-branch-trigger, .tree-leaf');
    if (!dragged || !row) return;
    const target = itemOf(row);
    if (target === dragged || dragged.contains(target)) return clearDrop(tree);
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const r = row.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    const isBranch = row.matches('.tree-branch-trigger');
    const where = isBranch ? (y < 0.25 ? 'before' : y > 0.75 ? 'after' : 'inside') : y < 0.5 ? 'before' : 'after';
    if (row.dataset.drop !== where) {
      clearDrop(tree);
      row.dataset.drop = where;
    }
  });
  tree.addEventListener('dragleave', (e) => {
    if (!tree.contains(e.relatedTarget)) clearDrop(tree);
  });
  tree.addEventListener('drop', (e) => {
    const row = tree.querySelector('[data-drop]');
    if (!dragged || !row) return;
    e.preventDefault();
    const target = itemOf(row);
    const where = row.dataset.drop;
    if (where === 'inside') {
      const details = target.querySelector(':scope > details');
      details.open = true;
      details.querySelector(':scope > .tree-group').append(dragged);
    } else {
      target.parentElement.insertBefore(dragged, where === 'before' ? target : target.nextSibling);
    }
    clearDrop(tree);
    announceMove(tree, dragged);
    if (tree.hasAttribute('data-checkable') && cascades(tree)) {
      tree.querySelectorAll('[role="treeitem"]').forEach((i) => { if (!childItems(i).length) rollUp(tree, i); });
      syncAria(tree);
    }
  });
  tree.addEventListener('dragend', () => {
    if (dragged) delete dragged.dataset.dragging;
    dragged = null;
    clearDrop(tree);
  });
}
/** Alt+ArrowUp / Alt+ArrowDown: move among siblings, keep focus. */
function moveByKey(tree, row, dir) {
  const item = itemOf(row);
  const sib = dir < 0 ? item.previousElementSibling : item.nextElementSibling;
  if (!sib) return;
  item.parentElement.insertBefore(item, dir < 0 ? sib : sib.nextSibling);
  row.focus();
  announceMove(tree, item);
}

function init() {
  document.querySelectorAll('.tree[role="tree"]:not([data-init])').forEach((tree) => {
    tree.dataset.init = '';
    const selectable = tree.hasAttribute('data-selectable');
    if (selectable) {
      // every operable item states its selection explicitly (authored
      // aria-selected="true" wins); disabled items carry none
      tree.querySelectorAll('[role="treeitem"]').forEach((item) => {
        if (!isDisabled(item) && !item.hasAttribute('aria-selected')) item.setAttribute('aria-selected', 'false');
      });
    }

    if (tree.hasAttribute('data-checkable')) initChecks(tree);
    if (tree.hasAttribute('data-sortable')) initSortable(tree);
    const checkable = tree.hasAttribute('data-checkable');

    /* Clicks: disabled rows neither toggle nor navigate; selectable trees
       select the clicked row (a branch toggles AND selects, like a file
       explorer). */
    tree.addEventListener('click', (e) => {
      const row = e.target.closest('.tree-branch-trigger, .tree-leaf');
      if (!row || !tree.contains(row)) return;
      const item = itemOf(row);
      if (isDisabled(item)) {
        e.preventDefault(); // <summary> would toggle, <a> would navigate
        return;
      }
      if (selectable) selectItem(tree, item);
      // a checkable leaf: clicking the row (not the box itself) ticks it
      const box = checkOf(item);
      if (checkable && !selectable && box && row.matches('.tree-leaf') && e.target !== box && !box.disabled) {
        box.checked = !box.checked;
        onCheck(tree, item);
      }
    });
    /* Keep aria-expanded in sync with <details> open state */
    tree.querySelectorAll('.tree-branch').forEach((details) => {
      const treeitem = details.closest('[role="treeitem"]');
      if (!treeitem) return;

      // snapshot the authored state + bind the api per branch:
      // `$('#my-branch').api.setState('expanded')`
      details._defaultOpen = details.open;
      details.api = {
        setState: (stateName, config) => treeViewApi.setState(details, stateName, config),
        getState: () => treeViewApi.getState(details),
      };

      details.addEventListener('toggle', () => {
        treeitem.setAttribute('aria-expanded', String(details.open));
        // user interaction also moves the named state (keeps getState honest)
        details.dataset.stateName = details.open ? 'expanded' : 'default';
      });
    });

    /* Keyboard navigation */
    tree.addEventListener('keydown', (e) => {
      const target = e.target.closest('.tree-branch-trigger, .tree-leaf');
      if (!target) return;

      const allItems = Array.from(tree.querySelectorAll('.tree-branch-trigger, .tree-leaf'));
      const visibleItems = allItems.filter((item) => item.checkVisibility());
      const index = visibleItems.indexOf(target);

      // sortable: Alt+ArrowUp / Alt+ArrowDown reorder among siblings
      if (e.altKey && tree.hasAttribute('data-sortable') && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        moveByKey(tree, target, e.key === 'ArrowUp' ? -1 : 1);
        return;
      }
      // checkable: Space ticks the row's box (a <summary> would toggle instead)
      if (e.key === ' ' && checkable) {
        const box = checkOf(itemOf(target));
        if (box && !box.disabled && !isDisabled(itemOf(target))) {
          e.preventDefault();
          box.checked = !box.checked;
          onCheck(tree, itemOf(target));
          return;
        }
      }
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (index < visibleItems.length - 1) visibleItems[index + 1].focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (index > 0) visibleItems[index - 1].focus();
          break;
        case 'ArrowRight':
          e.preventDefault();
          { const detailsR = target.closest('details.tree-branch');
          if (detailsR && !detailsR.open && !isDisabled(itemOf(target))) detailsR.open = true; }
          break;
        case 'Enter':
        case ' ': {
          const item = itemOf(target);
          // disabled: no native <summary> toggle, no link activation
          if (isDisabled(item)) {
            e.preventDefault();
            break;
          }
          if (!selectable) break;
          // a leaf span has no native activation - keep Space from scrolling
          if (target.matches('span.tree-leaf')) e.preventDefault();
          selectItem(tree, item);
          break;
        }
        case 'ArrowLeft':
          e.preventDefault();
          { const detailsL = target.closest('details.tree-branch');
          if (detailsL && detailsL.open) detailsL.open = false; }
          break;
        case 'Home':
          e.preventDefault();
          if (visibleItems.length) visibleItems[0].focus();
          break;
        case 'End':
          e.preventDefault();
          if (visibleItems.length) visibleItems[visibleItems.length - 1].focus();
          break;
      }
    });
});
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });
